import React, { useContext, useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  elements,
} from "chart.js";
import "chartjs-adapter-date-fns";
import ViewerContext from "../../context/ViewerContext";
import "./timeChart.css";
import { Cartesian3, JulianDate, Math as CesiumMath, ClockRange } from "cesium";
import { getDataSource, zoomToEntityBound } from "../../helper/cesiumHelper";
import { getSoloFlight, onSoloChange, dimmedCss } from "../../helper/soloMode";
import { isFlightHidden, onFlightVisibilityChange } from "../../helper/flightVisibility";

// Register the components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale
);

/**
 * Longest data gap the altitude curve is allowed to bridge, in ms.
 *
 * `spanGaps: true` draws a straight line across ANY hole, so a logger that was
 * off for two hours showed up as a clean diagonal climb — in the 10-pilot group
 * file two 127-minute holes were the most prominent "hikes" on the whole chart,
 * and neither happened. The chart was inventing content.
 *
 * 120 s is where the measured gap distribution splits. Fixes are 1 s apart
 * (median and p99 both 1 s), three of the ten loggers drop out systematically
 * for 45-46 s and 91-92 s (~230 occurrences — breaking those would shatter
 * their curves into ~160 pieces each), and the next gaps up are 130 s, 137 s,
 * 3.7 min, 8.7 min, 13.7 min and the two 127-minute holes. Nothing lands
 * between 92 s and 130 s.
 *
 * Chart.js compares this against the parsed x distance of consecutive points
 * (`maxGapLength` in core.controller), so it works on missing points, not just
 * explicit nulls — our data has no nulls, it simply stops.
 */
const MAX_GAP_MS = 120 * 1000;

let isDragging = false;
let isResizing = false;
let prevTime = 0;
const LineChart = () => {
  const { igcParsedData, coloredFile, fullRange, setFullRange } =
    useContext(ViewerContext);
  const [showChart, setShowChart] = useState();
  const [soloId, setSoloId] = useState(getSoloFlight());
  const [visibilityTick, setVisibilityTick] = useState(0);
  const chartRef = useRef(null);
  const stickRef = useRef(null);
  const mouseMovePositionRef = useRef(null);

  useEffect(() => {
    const onTick = viewer.clock.onTick.addEventListener(handleTimeUpdate);
    const offSolo = onSoloChange((id) => setSoloId(id));
    // Unchecking a pilot has to redraw: their curve goes, and the x axis is
    // recomputed from whoever is left.
    const offVisible = onFlightVisibilityChange(() => setVisibilityTick((t) => t + 1));

    // Cleanup when component unmounts
    return () => {
      onTick(); // Remove event listener
      offSolo();
      offVisible();
    };
  }, []);

  useEffect(() => {
    if (igcParsedData.length > 0) {
      setShowChart(true);
    }
  }, [igcParsedData]);

  useEffect(() => {
    let bottomValue = showChart ? 280 : 0;
    const chartContainer = document.getElementById("timeline-chart-container");
    if (chartContainer) {
      bottomValue =
        window.innerHeight - chartContainer.getBoundingClientRect().top;
      moveLogo(bottomValue, 10);
    } else {
      moveLogo(bottomValue);
    }
  }, [showChart]);

  const moveLogo = (bottomValue, offset = 65) => {
    const logoContainer = document.getElementsByClassName("logo-container");
    const scaleContainer = document.getElementsByClassName("distance-legend");

    if (logoContainer.length > 0) {
      logoContainer[0].style.setProperty("bottom", bottomValue + offset + "px");
    }

    if (scaleContainer.length > 0) {
      scaleContainer[0].style.setProperty(
        "bottom",
        bottomValue + offset + "px",
        "important"
      );
    }
  };
  const findDateRange = (data) => {
    const startEpochs = data.map((event) => event.start);
    const endEpochs = data.map((event) => event.end);

    const minStart = Math.min(...startEpochs);
    const maxEnd = Math.max(...endEpochs);

    const startDate = new Date(minStart * 1000);
    const endDate = new Date(maxEnd * 1000);

    return { startDate, endDate };
  };

  // Helper function to round date to nearest hour:half-hour
  const roundToNearestHalfHour = (date, status) => {
    const minutes = date.getMinutes();
    date.setSeconds(0);
    date.setMilliseconds(0);

    if (minutes == 30 || minutes == 0 || minutes == 60) {
      return date;
    }

    if (minutes < 30) {
      if (status == "end") date.setMinutes(30);
      else date.setMinutes(0);
    } else {
      if (status == "end") {
        date.setHours(date.getHours() + 1);
        date.setMinutes(0);
      } else {
        date.setMinutes(30);
      }
    }

    return date;
  };

  const generateEdgeDates = (startDate, endDate, intervalMinutes = 30) => {
    let current = roundToNearestHalfHour(startDate, "start");
    let end = roundToNearestHalfHour(endDate, "end");

    const sampleDates = [];
    const intervalMillis = intervalMinutes * 60 * 1000;

    while (current <= end) {
      sampleDates.push(current);
      current = new Date(current.getTime() + intervalMillis);
    }

    return sampleDates;
  };

  const handleTimeUpdate = () => {
    const chartInstance = chartRef.current;
    let time = viewer.clock.currentTime;
    if (time.secondsOfDay == prevTime) return;

    prevTime = time.secondsOfDay;
    if (chartInstance) {
      const jsDate = JulianDate.toDate(time);

      const currTime = jsDate.getTime();

      const chartArea = chartInstance.chartArea;
      const xScale = chartInstance.scales["x"];
      const pixelPosition = xScale.getPixelForValue(currTime);

      if (pixelPosition) {
        if (stickRef.current) {
          const left = pixelPosition + 15;
          const top = chartArea.top;
          const bottom = chartArea.bottom;
          const height = chartArea.bottom - chartArea.top;
          moveTimeStick(stickRef.current, left, top, bottom, height);
        }
      }
    }
  };

  const handleMouseClick = (event, elements) => {
    // In solo mode the other pilots are context, not targets. A click on their
    // curve falls through to the scrub-the-time branch below, so the chart stays
    // usable everywhere — it just can't hand them the camera.
    const solo = getSoloFlight();
    const picked = solo
      ? elements.filter((e) => igcParsedData[e.datasetIndex]?.id === solo)
      : elements;

    if (picked.length > 0) {
      const rawData = igcParsedData[picked[0].datasetIndex].data[picked[0].index];
      const newDate = JulianDate.fromDate(new Date(rawData.epoch * 1000));
      viewer.clock.currentTime = newDate;

      const fileId = igcParsedData[picked[0].datasetIndex].id;
      const dataSource = getDataSource(fileId, coloredFile, igcParsedData);
      if (dataSource) {
        let entities = dataSource.entities.values;
        if (entities.length > 0) {
          const entity = entities[0];
          zoomToEntityBound(entity);
        }
      }
    } else {
      const chartInstance = chartRef.current;
      const xScale = chartInstance.scales.x;
      const clientRect = event.native.target.getBoundingClientRect();
      const xOffset = event.native.clientX - clientRect.left;
      const xValue = xScale.getValueForPixel(xOffset);

      if (xValue !== null) {
        const newTimestamp = new Date(Math.round(xValue));
        const newDate = JulianDate.fromDate(newTimestamp);
        viewer.clock.currentTime = newDate;
      }
    }
  };

  const handleOnTouchMove = (event) => {
    if (chartRef.current) {
      const chartInstance = chartRef.current;
      const chartArea = chartInstance.chartArea;

      if (event.type == "touchmove") {
        const nativeEvent = event.touches[0];

        if (
          nativeEvent.pageX >= chartArea.left &&
          nativeEvent.pageX <= chartArea.right
        ) {
          moveTimeStickHandler(nativeEvent, chartInstance, nativeEvent.pageX);
        }
      }
    }
  };
  const handleMouseOver = (event, elements) => {
    if (chartRef.current) {
      const chartInstance = chartRef.current;
      const chartArea = chartInstance.chartArea;

      if (
        event.native.offsetX >= chartArea.left &&
        event.native.offsetX <= chartArea.right &&
        event.native.offsetY >= chartArea.top &&
        event.native.offsetY <= chartArea.bottom
      ) {
        moveTimeStickHandler(event.native, chartInstance, event.native.x);
      }
    }
  };

  const moveTimeStickHandler = (nativeEvent, chartInstance, left) => {
    const chartArea = chartInstance.chartArea;
    const top = chartArea.top;
    const bottom = chartArea.bottom;
    const height = chartArea.height;
    if (isDragging) {
      mouseMovePositionRef.current.style.setProperty("left", -10 + "px");
      moveTimeStick(stickRef.current, left, top, bottom, height);
      const xScale = chartInstance.scales.x;
      const clientRect = nativeEvent.target.getBoundingClientRect();
      const xOffset = nativeEvent.clientX - clientRect.left;
      const xValue = xScale.getValueForPixel(xOffset);

      if (xValue !== null) {
        const newTimestamp = new Date(Math.round(xValue));
        const newDate = JulianDate.fromDate(newTimestamp);
        viewer.clock.currentTime = newDate;
      }
    } else {
      moveTimeStick(mouseMovePositionRef.current, left, top, bottom, height);
    }
  };

  const handleMouseDown = (event) => {
    isDragging = true;
  };

  const handleMouseUp = (event) => {
    isDragging = false;
  };

  const handleResizeMouseDown = (e) => {
    const chartContainer = document.getElementById("timeline-chart-container");

    if (!e.touches) e.preventDefault();
    isResizing = true;
    stickRef.current.style.setProperty("left", -10 + "px");

    function doDrag(e) {
      let clientY;
      if (e.touches) {
        clientY = e.touches[0].clientY;
      } else {
        clientY = e.clientY;
      }

      const windowHeight = window.innerHeight;
      const newHeight = windowHeight - clientY - 60;

      //If size is less then 50 hide the chart
      chartRef.current.attached = newHeight < 50 ? false : true;

      if (newHeight > 0 && newHeight + 150 < windowHeight) {
        chartContainer.style.height = newHeight + "px";
        moveLogo(newHeight);
      }
    }

    function stopDrag() {
      isResizing = false;
      prevTime = 0;
      document.documentElement.removeEventListener("mousemove", doDrag, false);
      document.documentElement.removeEventListener("mouseup", stopDrag, false);
      document.documentElement.removeEventListener("touchmove", doDrag, false);
      document.documentElement.removeEventListener("touchend", stopDrag, false);
    }

    document.documentElement.addEventListener("mousemove", doDrag, false);
    document.documentElement.addEventListener("mouseup", stopDrag, false);
    document.documentElement.addEventListener("touchmove", doDrag, false);
    document.documentElement.addEventListener("touchend", stopDrag, false);
  };

  const handleMouseLeave = (event) => {
    isDragging = false;
    mouseMovePositionRef.current.style.setProperty("left", -10 + "px");
  };

  const moveTimeStick = (stickObj, left, top, bottom, height) => {
    if (isResizing) return;

    //If chart is hidden, hide the stick obj
    if (!chartRef.current.attached) {
      stickObj.style.setProperty("left", -10 + "px");
      return;
    }

    if (stickObj) {
      stickObj.style.setProperty("left", left + "px");
      stickObj.style.setProperty("top", top + 15 + "px");
      stickObj.style.setProperty("bottom", bottom - 5 + "px");
      stickObj.style.setProperty("height", height - 20 + "px");
    }
  };

  const createLineChart = () => {
    // The time range spans the flights that are actually SHOWN — unchecking the
    // pilot whose logger ran longest has to shrink the axis, not leave hours of
    // empty chart behind. With nothing left checked there is no range to
    // compute, so fall back to all of them rather than producing NaN bounds.
    const visible = igcParsedData.filter((item) => !isFlightHidden(item.id));
    const inRange = visible.length > 0 ? visible : igcParsedData;

    const boundTimeSet = [];
    inRange.forEach((item) => {
      // The flight's content window, not its recorded span: loggers are switched
      // on at home and off in the evening (helper/contentWindow.js). Falls back
      // to the full span for tracks too short to say anything about, and when
      // the user asks for the whole recording via the timeline bar.
      const content = fullRange ? null : item.contentWindow;
      boundTimeSet.push({
        start: content ? content.start : item.data[0].epoch,
        end: content ? content.end : item.data[item.data.length - 1].epoch,
      });
    });
    const dateRange = findDateRange(boundTimeSet);
    const labels = generateEdgeDates(dateRange.startDate, dateRange.endDate);

    const startTime = JulianDate.fromIso8601(dateRange.startDate.toISOString());
    const stopTime = JulianDate.fromIso8601(dateRange.endDate.toISOString());

    // Set Cesium clock with the start and stop time
    viewer.clock.startTime = startTime;
    viewer.clock.stopTime = stopTime;

    const chartDataConfig = {
      labels: labels,
      datasets: igcParsedData.map((item) => {
        // Solo mode fades the other curves the same way it fades their tracks
        // (helper/soloMode.js), so the chart and the globe agree.
        const faded = soloId != null && item.id !== soloId;
        return {
          label: item.pilotName,
          // Hidden rather than removed from the array: Chart.js reports clicks
          // by `datasetIndex`, which handleMouseClick uses to index straight
          // into igcParsedData. Filtering here would silently shift that
          // mapping. Hidden datasets are excluded from picking anyway.
          hidden: isFlightHidden(item.id),
          data: item.data
            .filter((point) => point.altitude > 0) // Filter out 0 and negative altitude values
            .map((point) => ({
              x: new Date(point.epoch * 1000),
              y: point.altitude,
            })),
          borderColor: faded ? dimmedCss(item.color) : `rgba(${item.color.join(",")})`,
          borderWidth: faded ? 1.5 : 4,
          // Lowest order draws LAST in Chart.js (`_drawDatasets` iterates the
          // order-sorted metasets backwards), so this puts the soloed curve on
          // top of the faded ones instead of under whichever came later.
          order: faded ? 1 : 0,
          pointRadius: 0, // disable points for a single dataset
          backgroundColor: "rgba(0, 0, 0, 0)", // Transparent background
          tension: 0.4, // Optional: curve lines,
          spanGaps: MAX_GAP_MS, // break real holes instead of drawing across them
        };
      }),
    };

    const chartOptions = {
      animation: false,
      datasets: {
        line: {
          pointRadius: 0, // disable for all `'line'` datasets
        },
      },
      scales: {
        x: {
          type: "time",
          bounds: "ticks",
          min: labels[0],
          max: labels[labels.length - 1],
          time: {
            unit: "minute",
            displayFormats: {
              minute: "HH:mm",
            },
            round: true,
            // parser: parseLabelData,
            tooltipFormat: "HH:mm",
          },
          ticks: {
            // source: 'labels',
            maxTicksLimit: 20,
            stepSize: 30,
            color: "#ffffff", // Set the color of the x-axis
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            display: true,
            color: "rgba(200, 200, 200, 0.5)",
          },
          min: 0,
          // max: 10000,
          ticks: {
            color: "#ffffff", // Set the color of the x-axis
          },
        },
      },
      onClick: (event, elements) => {
        handleMouseClick(event, elements);
      },
      onHover: (event, elements) => {
        handleMouseOver(event, elements);
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: () => {
              return null;
            },
            footer: (context) => {
              let label = context[0].dataset.label || "";

              if (label) {
                label += ": " + context[0].dataset.data[context[0].dataIndex].y;
              }

              return label;
            },
            title: () => {
              return null;
            },
          },
        },
      },
      responsive: true,
      maintainAspectRatio: false,
    };

    return (
      <Line
        ref={chartRef}
        className="chart"
        data={chartDataConfig}
        options={chartOptions}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
        onTouchCancel={handleMouseLeave}
        onTouchMove={handleOnTouchMove}
      />
    );
  };

  return (
    <>
      {igcParsedData && igcParsedData.length > 0 && (
        <div className="timeline-chart-container" id="timeline-chart-container">
          <div
            id="chart-resizer"
            onMouseDown={handleResizeMouseDown}
            onTouchStart={handleResizeMouseDown}
          ></div>
          {/* The way back out of the focused time window (helper/contentWindow.js).
              It floats over the chart rather than sitting in the timeline bar:
              that row is the tightest thing on a phone already, and this control
              belongs next to the axis it changes. */}
          <div className="chart-overlay">
            <button
              onClick={(e) => {
                e.currentTarget.blur();
                setFullRange(!fullRange);
              }}
              title={
                fullRange
                  ? "Showing the whole recording — back to the flying"
                  : "Show the whole recording, parked logger included"
              }
            >
              <img
                src="./icons/full-range.svg"
                alt="Full recorded range"
                style={{ opacity: fullRange ? 1 : 0.5 }}
              />
            </button>
          </div>
          {createLineChart()}
          <div className="vertical-line" ref={stickRef}></div>

          <div className="vertical-line-low" ref={mouseMovePositionRef}></div>
        </div>
      )}
    </>
  );
};

export default LineChart;
